import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { CollectionState, ElectionType } from '../../api/api-types';
import {
  SchemaPartyDetailsResponse,
  SchemaUserDeclaration,
} from '../../api/api-schema';
import { fetcher } from '../../api/fetcher';
import Button from '../../components/Button/Button';
import Modal from '../../components/Modal/Modal';
import Placeholder from '../../components/Placeholder/Placeholder';
import RichText from '../../components/RichText/RichText';
import { getErrorWithState } from '../../components/StatusMessages/StatusMessageOverlay';
import { useDictionary } from '../../hooks/useDictionary';
import { rem } from '../../styles/rem';
import { ButtonVariant } from '../../styles/buttons';
import {
  collectionStateByElectionType,
  getUnavailableActionState,
  GuardedDeclarationAction,
} from '../../utils/declarationActionState';

export { collectionStateByElectionType, getUnavailableActionState };

const fullWidthButtonQuery = '@media (max-width: 47.99em)';

type StaleActionGuardProps = {
  action?: GuardedDeclarationAction;
  children: (unavailableState: CollectionState | undefined) => React.ReactNode;
  collectionState?: CollectionState;
  electionType?: ElectionType;
  partyId?: string;
  userDeclarations?: SchemaUserDeclaration[] | null;
};

export function ButtonsSection({
  button,
  cancel,
  stacked,
}: {
  button?: React.ReactNode | React.ReactNode[];
  cancel?: React.ReactNode;
  stacked?: boolean;
}) {
  const renderPrimary = () => {
    if (Array.isArray(button))
      return button.map((b, i) => <React.Fragment key={i}>{b}</React.Fragment>);
    return button || null;
  };

  return (
    <div
      sx={{
        display: 'flex',
        flexDirection: stacked ? 'column' : ['column', 'row'],
        alignItems: stacked ? 'flex-start' : ['stretch', 'center'],
        gap: [rem(12), rem(16)],
        mt: [rem(32), rem(32), rem(24)],
        '> form': {
          width: 'auto',
          [fullWidthButtonQuery]: {
            width: '100%',
          },
        },
        '> form button, > a, > button': {
          width: 'auto',
          [fullWidthButtonQuery]: {
            width: '100%',
          },
          whiteSpace: stacked ? ['normal', 'nowrap'] : undefined,
          mr: 0,
        },
      }}
    >
      {renderPrimary()}
      {cancel ? (
        typeof cancel === 'string' || typeof cancel === 'number' ? (
          <Button
            variant={ButtonVariant.Flat}
            sx={{
              width: 'auto',
              [fullWidthButtonQuery]: {
                width: '100%',
              },
            }}
            onClick={() => window.history.back()}
          >
            {cancel}
          </Button>
        ) : (
          <>{cancel}</>
        )
      ) : null}
    </div>
  );
}

export function StaleActionGuard({
  action,
  children,
  collectionState,
  electionType,
  partyId,
  userDeclarations,
}: StaleActionGuardProps) {
  const { t } = useDictionary();
  const [dismissedStaleActionKey, setDismissedStaleActionKey] = useState<
    string | undefined
  >();
  const shouldValidateAction =
    action !== undefined &&
    !!partyId &&
    electionType !== undefined &&
    electionType !== ElectionType.BothTypes;
  const shouldFetchCollectionState =
    shouldValidateAction && collectionState === undefined;
  const staleActionKey = [action, partyId, electionType].join(':');
  const {
    data: partyDetails,
    status: partyDetailsStatus,
    isFetching: isFetchingPartyDetails,
  } = useQuery<SchemaPartyDetailsResponse>(
    ['dialog-action-party', action, partyId, electionType],
    () => fetcher<SchemaPartyDetailsResponse>(`/api/v1/parties/${partyId}`),
    {
      enabled: shouldFetchCollectionState,
      staleTime: 0,
    },
  );
  const isValidatingAction =
    shouldFetchCollectionState &&
    (partyDetailsStatus === 'loading' || isFetchingPartyDetails);
  const selectedDeclaration = userDeclarations?.find(
    (declaration) =>
      declaration.partyId === partyId &&
      declaration.electionType === electionType,
  );
  const validatedCollectionState =
    collectionState !== undefined
      ? collectionState
      : shouldFetchCollectionState && partyDetailsStatus === 'success'
      ? collectionStateByElectionType(partyDetails, electionType)
      : undefined;
  const unavailableActionState = getUnavailableActionState(
    action,
    selectedDeclaration,
    validatedCollectionState,
  );
  const showUnavailableActionModal =
    unavailableActionState !== undefined &&
    dismissedStaleActionKey !== staleActionKey;

  if (isValidatingAction) return <Placeholder css={{ height: 120 }} />;

  return (
    <>
      {children(unavailableActionState)}
      <Modal
        isOpen={showUnavailableActionModal}
        onDismiss={() => setDismissedStaleActionKey(staleActionKey)}
        title={t('errors.generic_title', { asString: true })}
      >
        <RichText>
          {t(
            getErrorWithState(
              unavailableActionState,
              action,
              'errors.generic_text',
            ),
          )}
        </RichText>
      </Modal>
    </>
  );
}
